import { Link, useLocation } from 'react-router-dom';
import { Icon } from '@/shared/ui/icon';
import { useAuth } from '@/features/identity-management/auth/hooks/useAuth';
import { useT, type MessageKey } from '@/shared/i18n';
import { useToast } from '@/shared/ui/primitives';
import { filterAppNavItems, type AppNavItem } from './app-nav';
import type { ReactNode } from 'react';

const icons: Record<string, ReactNode> = {
  workspace: <Icon name="dashboard" size={20} />,
  profile: <Icon name="user" size={20} />,
  'smart-profile': <Icon name="sparkles" size={20} />,
  'cv-intelligence': <Icon name="file-text" size={20} />,
  'job-matches': <Icon name="brain" size={20} />,
  applications: <Icon name="briefcase" size={20} />,
  company: <Icon name="building" size={20} />,
  'employer-dashboard': <Icon name="dashboard" size={20} />,
  'company-profile': <Icon name="building" size={20} />,
  jobs: <Icon name="briefcase" size={20} />,
  'employer-applications': <Icon name="clipboard-list" size={20} />,
  'find-candidates': <Icon name="search" size={20} />,
  settings: <Icon name="settings" size={20} />,
  admin: <Icon name="shield" size={20} />,
  'admin-dashboard': <Icon name="dashboard" size={20} />,
  'admin-users': <Icon name="users" size={20} />,
  'admin-roles': <Icon name="shield" size={20} />,
  'admin-plans': <Icon name="credit-card" size={20} />,
  'admin-ai-usage': <Icon name="brain" size={20} />,
  'admin-early-access': <Icon name="mail" size={20} />,
  'admin-settings': <Icon name="sliders" size={20} />,
  'admin-audit-logs': <Icon name="audit" size={20} />,
};

function NavItem({
  item,
  collapsed,
  onNavigate,
}: {
  item: AppNavItem;
  collapsed: boolean;
  onNavigate?: (() => void) | undefined;
}): ReactNode {
  const { t } = useT();
  const { notify } = useToast();
  const location = useLocation();
  const itemPath = item.to?.split('#')[0];
  const itemHash = item.to?.includes('#') ? item.to.slice(item.to.indexOf('#')) : '';
  const isActive =
    Boolean(item.to) &&
    itemPath === location.pathname &&
    (itemHash ? itemHash === location.hash : location.hash === '');
  const label = t(item.labelKey);
  const showChip = Boolean(item.chipKey) && !item.isLive;

  // Icon-rail tooltip (collapsed only): a popup label that slides in on hover.
  const tooltip =
    'pointer-events-none absolute start-[calc(100%+0.75rem)] top-1/2 z-40 -translate-y-1/2 whitespace-nowrap rounded-md border border-[var(--ink-band-border)] bg-[var(--ink-band-raised)] px-3 py-1.5 text-xs font-semibold text-[var(--on-ink-band)] opacity-0 shadow-panel transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100';

  const collapsedShell =
    'group relative mx-auto flex h-11 w-11 items-center justify-center rounded-lg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary-fixed/50';
  const expandedShell =
    'group relative flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-start transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary-fixed/50';

  const activeTone = isActive
    ? collapsed
      ? 'bg-[#cccccb1f] text-white'
      : 'bg-[#cccccb1f] text-white'
    : 'text-on-primary-container hover:bg-white/5 hover:text-white';
  const disabledTone =
    'text-on-primary-container hover:bg-white/5 hover:text-white';

  const expandedLabel = collapsed ? null : (
    <span className="flex min-w-0 flex-1 items-center justify-between gap-2">
      <span className="block truncate text-sm font-semibold">{label}</span>
      {showChip ? (
        <span className="shrink-0 rounded-full border border-[var(--ink-band-border)] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-[var(--on-ink-band-variant)]">
          {t(item.chipKey as MessageKey)}
        </span>
      ) : null}
    </span>
  );

  // Items without a route fall back to a "reserved module" toast. With every
  // reserved PRD module now navigable (isPreview), this only guards genuinely
  // route-less items.
  if (!item.to) {
    return (
      <button
        type="button"
        aria-label={collapsed ? label : undefined}
        title={collapsed ? label : undefined}
        onClick={() =>
          notify({
            tone: 'info',
            title: t('appShell.reserved.module.title'),
            description: t(item.descriptionKey),
          })
        }
        className={`${collapsed ? collapsedShell : expandedShell} ${disabledTone}`}
      >
        <span className="flex h-6 w-6 shrink-0 items-center justify-center">
          {icons[item.key]}
        </span>
        {expandedLabel}
        {collapsed ? <span className={tooltip}>{label}</span> : null}
      </button>
    );
  }

  return (
    <Link
      to={item.to ?? '/app'}
      onClick={onNavigate}
      aria-current={isActive ? 'page' : undefined}
      aria-label={collapsed ? label : undefined}
      title={collapsed ? label : undefined}
      className={`${collapsed ? collapsedShell : expandedShell} ${activeTone}`}
    >
      <span className="flex h-6 w-6 shrink-0 items-center justify-center">
        {icons[item.key]}
      </span>
      {expandedLabel}
      {collapsed ? <span className={tooltip}>{label}</span> : null}
    </Link>
  );
}

/**
 * Workspace navigation for the authenticated shell.
 *
 * `collapsed` (the desktop icon rail) renders centered 44px icon buttons with
 * slide-in tooltips; expanded (the mobile drawer) renders a labelled list.
 * `settings` is pinned to the bottom, divided from the primary modules.
 */
export function AppSidebar({
  collapsed = false,
  onNavigate,
}: {
  collapsed?: boolean;
  onNavigate?: () => void;
}): ReactNode {
  const { t } = useT();
  const { user } = useAuth();
  const items = filterAppNavItems(user);
  const mainItems = items.filter((item) => item.key !== 'settings');
  const settingsItem = items.find((item) => item.key === 'settings');

  return (
    <div className="flex h-full flex-col">
      <nav
        aria-label={t('appShell.nav.label')}
        className={collapsed ? 'flex flex-col gap-2' : 'space-y-1'}
      >
        {mainItems.map((item) => (
          <NavItem
            key={item.key}
            item={item}
            collapsed={collapsed}
            onNavigate={onNavigate}
          />
        ))}
      </nav>

      {settingsItem ? (
        <div
          className={`mt-auto w-full border-t border-[var(--ink-band-border)] pt-4 ${
            collapsed ? '' : 'space-y-1'
          }`}
        >
          <NavItem item={settingsItem} collapsed={collapsed} onNavigate={onNavigate} />
        </div>
      ) : null}
    </div>
  );
}
